import React from "react";

import {useSiteContext} from "@client/site/context";
import {classNames} from "@reactivated";

interface Props {
    columns: Array<{
        name: string;
        accessor: string;
        // Will not have the hover / inset layer on top.
        type?: "actions";
        headerClassName?: string;
        dataClassName?: string;
    }>;
    values: Array<Record<string, string | React.ReactNode | null> & {link?: string}>;
}

function getNavBarHeight(realm: "app" | "site") {
    if (realm == "app") {
        const element = document.getElementById("app-scroller")!;
        return (
            element.getBoundingClientRect().top -
            parseFloat(window.getComputedStyle(element).paddingTop)
        );
    }

    return window.innerWidth >= 1024 ? 0 : 64;
}

export const ManagerTable = (props: Props) => {
    const tableRef = React.useRef<HTMLDivElement>(null);
    const context = useSiteContext();

    React.useEffect(() => {
        const domElement =
            context.realm == "app" ? document.getElementById("app-scroller")! : window;
        const stickyNavBar = getNavBarHeight(context.realm);

        const handleScroll = () => {
            const tableContainer = tableRef.current;
            if (!tableContainer) {
                return;
            }

            const tableHeader = tableContainer.querySelector("thead");
            if (!tableHeader) {
                return;
            }

            if (tableRef.current == null) {
                return;
            }

            const top = tableRef.current.getBoundingClientRect().top - stickyNavBar;

            if (top < 0) {
                const translate = `translate(0, ${Math.abs(top)}px)`;
                tableHeader.style.transform = translate;
            } else {
                tableHeader.style.transform = "";
            }
        };

        domElement.addEventListener("scroll", handleScroll);

        return () => {
            domElement.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const tableClasses = classNames(
        "overflow-x-auto",
        context.realm == "site" && "-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8",
        context.realm == "app" && "-mx-[10px]",
    );

    return (
        <div className="flow-root" ref={tableRef}>
            <div className={tableClasses}>
                <div
                    className="
                        inline-block min-w-full py-2 align-middle
                        sm:px-6
                        lg:px-8
                    "
                >
                    <div
                        className="
                            overflow-hidden shadow-sm ring-1 ring-black/5
                            sm:rounded-lg
                        "
                    >
                        <table
                            className="
                                min-w-full divide-y divide-gray-300 whitespace-nowrap
                            "
                        >
                            <thead className="relative z-20 bg-white">
                                <tr>
                                    {props.columns.map((column, index) => (
                                        <th
                                            key={index}
                                            scope="col"
                                            className={classNames(
                                                "px-3 py-3.5 text-left text-sm font-semibold text-gray-900",
                                                index == 0 && "pl-8",
                                                column.headerClassName,
                                            )}
                                        >
                                            {column.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {props.values.map((value, index) => (
                                    <tr key={index} className="hover:bg-indigo-100/20">
                                        {props.columns.map((column, index) => (
                                            <td
                                                key={index}
                                                className={classNames(
                                                    "relative whitespace-nowrap px-3 py-4 text-sm",
                                                    index == 0
                                                        ? "pl-8 font-medium text-gray-900"
                                                        : "text-gray-500",
                                                    column.dataClassName,
                                                )}
                                            >
                                                {column.type !== "actions" && (
                                                    <a
                                                        aria-hidden={true}
                                                        className="absolute inset-0"
                                                        href={value.link}
                                                    />
                                                )}
                                                {index == 0 && value.link != null ? (
                                                    <a href={value.link}>
                                                        {value[column.accessor]}
                                                    </a>
                                                ) : (
                                                    value[column.accessor]
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
